import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateLocale } from '@/lib/locale';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Search, Eye, Filter } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SaleDetailDialog } from '@/components/sales/SaleDetailDialog';
import { startOfDay, startOfWeek, startOfMonth, isAfter } from 'date-fns';
import { useMerchantId } from '@/hooks/useMerchantId';
import { usePagination } from '@/hooks/use-pagination';
import { PaginationControls } from '@/components/PaginationControls';
import { BranchFilter } from '@/components/BranchFilter';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PER_PAGE } from '@/lib/fetch-all';

type Sale = components['schemas']['SaleResource'];


export default function SalesList() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [combinedFilter, setCombinedFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [branchFilter, setBranchFilter] = useState('all');
  const { merchantId } = useMerchantId();

  // Derive filter and dateFilter from combinedFilter
  const paymentFilter = combinedFilter.startsWith('p:') ? combinedFilter.slice(2) : 'all';
  const dateFilter = combinedFilter.startsWith('d:') ? combinedFilter.slice(2) : 'all';

  const { data: sales = [], isLoading } = useQuery({
    queryKey: ['sales', merchantId],
    queryFn: async () => {
      const all: Sale[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const { data, error } = await apiClient.GET('/sales', { params: { query: { page, per_page: FETCH_ALL_PER_PAGE } } });
        if (error) throw error;
        all.push(...(data?.data ?? []));
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return all;
    },
    enabled: !!merchantId,
  });

  const filtered = sales.filter((s) => {
    // Payment filter
    if (paymentFilter === 'murabaha') {
      if (!s.is_murabaha) return false;
    } else if (paymentFilter !== 'all') {
      if (s.payment_method !== paymentFilter) return false;
    }
    if (search && !String(s.id).includes(search)) return false;
    if (branchFilter !== 'all' && String(s.branch_id) !== branchFilter) return false;

    // Date filter
    if (dateFilter !== 'all') {
      const saleDate = new Date(s.created_at);
      const now = new Date();
      if (dateFilter === 'today' && !isAfter(saleDate, startOfDay(now))) return false;
      if (dateFilter === 'week' && !isAfter(saleDate, startOfWeek(now, { weekStartsOn: 1 }))) return false;
      if (dateFilter === 'month' && !isAfter(saleDate, startOfMonth(now))) return false;
    }
    return true;
  });

  const pagination = usePagination(filtered, 25);

  const salesExportData = filtered.map((s) => ({
    id: s.id,
    sana: new Date(s.created_at).toLocaleDateString(dateLocale()),
    tovarlar: (s.items || [])
      .map((i) => {
        const sku = i.product?.sku ? ` (${i.product.sku})` : '';
        const mhik = i.mhik ? ` [MXIK: ${i.mhik}]` : '';
        return `${i.product_name}${sku}${mhik} × ${Number(i.quantity)}`;
      })
      .join('; '),
    filial: s.branch?.name || '—',
    kassir: s.cashier?.name || '—',
    summa: Number(s.total_amount),
    tolov: s.is_murabaha ? t('misc_common.murabaha') : s.payment_method === 'cash' ? t('misc_common.pay_cash') : s.payment_method === 'card' ? t('misc_common.pay_card') : t('misc_common.pay_wallet'),
  }));

  const salesExportColumns = [
    { header: 'ID', key: 'id' },
    { header: t('misc_sales.date'), key: 'sana' },
    { header: t('misc_sales.items'), key: 'tovarlar' },
    { header: t('misc_sales.branch'), key: 'filial' },
    { header: t('misc_sales.cashier'), key: 'kassir' },
    { header: t('misc_sales.amount'), key: 'summa' },
    { header: t('misc_sales.payment_type'), key: 'tolov' },
  ];


  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-foreground">{t('misc_sales.title')}</h1>
        <ExportButtons data={salesExportData} columns={salesExportColumns} fileName="savdolar" title={t('misc_sales.export_title')} />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('misc_sales.search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <BranchFilter value={branchFilter} onChange={setBranchFilter} />
        <Select value={combinedFilter} onValueChange={setCombinedFilter}>
          <SelectTrigger className="w-[160px]">
            <Filter size={14} className="text-muted-foreground mr-1" />
            <SelectValue placeholder={t('misc_sales.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('misc_sales.all')}</SelectItem>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('misc_sales.payment_type')}</SelectLabel>
              <SelectItem value="p:cash">{t('misc_common.pay_cash')}</SelectItem>
              <SelectItem value="p:card">{t('misc_common.pay_card')}</SelectItem>
              <SelectItem value="p:hisob_wallet">{t('misc_dash.wallet')}</SelectItem>
              <SelectItem value="p:murabaha">{t('misc_common.murabaha')}</SelectItem>
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('misc_sales.date')}</SelectLabel>
              <SelectItem value="d:today">{t('misc_sales.today')}</SelectItem>
              <SelectItem value="d:week">{t('misc_sales.week')}</SelectItem>
              <SelectItem value="d:month">{t('misc_sales.month')}</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground p-3">ID</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.date')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.items')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.category')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.branch')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.amount')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_sales.payment_type')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">{t('misc_common.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">{t('misc_sales.empty')}</td></tr>
              ) : (
                pagination.items.map((s) => {
                  const items = s.items || [];
                  const productNames = items.map((i) => i.product_name).join(', ');
                  const categorySet = new Set(items.map((i) => i.product?.category).filter((c): c is string => !!c));
                  const categoryNames = categorySet.size > 0 ? Array.from(categorySet).join(', ') : '—';

                  const paymentLabel = s.is_murabaha
                    ? t('misc_common.murabaha')
                    : s.payment_method === 'cash' ? t('misc_common.pay_cash')
                    : s.payment_method === 'card' ? t('misc_common.pay_card')
                    : s.payment_method === 'hisob_wallet' ? t('misc_common.pay_wallet')
                    : s.payment_method;

                  const paymentVariant = s.is_murabaha ? 'outline' as const : 'secondary' as const;

                  return (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm text-foreground font-mono">{s.id}</td>
                      <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">{new Date(s.created_at).toLocaleDateString(dateLocale())}</td>
                      <td className="p-3 text-sm text-foreground max-w-[200px] truncate" title={productNames}>{productNames || '—'}</td>
                      <td className="p-3 text-sm text-muted-foreground">{categoryNames}</td>
                      <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">{s.branch?.name || '—'}</td>
                      <td className="p-3 text-sm font-medium text-foreground whitespace-nowrap">{formatUZS(Number(s.total_amount))}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant={paymentVariant} className={`text-xs ${s.is_murabaha ? 'border-orange-500/50 text-orange-600 bg-orange-500/10' : ''}`}>
                            {paymentLabel}
                          </Badge>
                          {(s.payments || []).length > 1 && (
                            <Badge variant="outline" className="text-xs">{t('misc_sales.mixed')}</Badge>
                          )}
                          {s.status === 'refunded' && (
                            <Badge variant="outline" className="text-xs border-destructive/50 text-destructive bg-destructive/10">{t('misc_sales.refunded')}</Badge>
                          )}
                          {s.status === 'voided' && (
                            <Badge variant="outline" className="text-xs">{t('misc_sales.voided')}</Badge>
                          )}
                        </div>
                      </td>

                      <td className="p-3">
                        <button onClick={() => setSelectedSale(s)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground">
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} pageSize={pagination.pageSize} onPrev={pagination.prevPage} onNext={pagination.nextPage} hasPrev={pagination.hasPrev} hasNext={pagination.hasNext} />
      </div>

      <SaleDetailDialog sale={selectedSale} onClose={() => setSelectedSale(null)} />

    </div>
  );
}
